import React, { useState, useEffect, useCallback } from 'react';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { BASE_URL } from '../constants/config';
import { showToast, showAlert } from '../components/Toast';
import AppShell from '../components/AppShell';
import Modal from '../components/Modal';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${dd} ${monthNames[d.getMonth()]}, ${d.getFullYear()}`;
};

const formatINR = (value) => {
  if (value === null || value === undefined) return '₹ 0.00';
  return '₹ ' + Math.abs(value).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
};

const getCategoryIcon = (category) => {
  const map = {
    'Job Work': { icon: '🔨', color: '#5D4037' },
    'Raw Material': { icon: '📦', color: '#607D8B' },
    'Transportation': { icon: '🚚', color: '#1976D2' },
    'Stationary': { icon: '✏️', color: '#FB8C00' },
    'Ready Item Purchase': { icon: '🛍️', color: '#E91E63' },
    'Salary': { icon: '💰', color: '#4CAF50' },
    'Chrome': { icon: '⚗️', color: '#9C27B0' },
    'Furniture': { icon: '🪑', color: '#795548' },
    'Courier': { icon: '📮', color: '#FF9800' },
    'Maintainance': { icon: '🔧', color: '#F44336' },
    'Casting': { icon: '🔥', color: '#FF5722' },
    'Office Expense': { icon: '🏢', color: '#3F51B5' },
    'Packaging & parcel': { icon: '📦', color: '#8BC34A' },
    'Master Charge': { icon: '⭐', color: '#FFD600' },
    'Charges': { icon: '💳', color: '#2196F3' },
    'Tour': { icon: '✈️', color: '#03A9F4' },
    'Donation': { icon: '🤝', color: '#F06292' },
    'Tool for Work': { icon: '🛠️', color: '#455A64' },
    'Service Charge': { icon: '🛎️', color: '#009688' },
    'Refund': { icon: '↩️', color: '#673AB7' },
    'Tax': { icon: '%', color: '#FF5252' },
    'Software': { icon: '💻', color: '#00BCD4' },
    'Samples': { icon: '🧪', color: '#CDDC39' },
    'Gift Product': { icon: '🎁', color: '#FF4081' },
    'Loan': { icon: '🏦', color: '#7E57C2' },
  };
  return map[category || ''] || { icon: '💸', color: '#888' };
};

const groupExpensesByDate = (data) => {
  const groups = {};
  data.forEach(item => {
    const dateStr = formatDate(item.Date);
    if (!groups[dateStr]) groups[dateStr] = [];
    groups[dateStr].push(item);
  });

  const todayStr = formatDate(new Date().toISOString());

  return Object.keys(groups).sort((a, b) => {
    const dateA = new Date(groups[a][0].Date || '').getTime();
    const dateB = new Date(groups[b][0].Date || '').getTime();
    return dateB - dateA;
  }).map(dateStr => {
    const sectionData = groups[dateStr];
    const total = sectionData.reduce((sum, item) => sum - (item.Amount || 0), 0);
    return {
      title: dateStr === todayStr ? 'Today' : dateStr,
      total,
      data: sectionData,
    };
  });
};

// ── Component ─────────────────────────────────────────────────────────────────

const ExpenseListScreen = ({ navigate, onLogout }) => {
  const [expenses, setExpenses] = useState([]);
  const [balanceInfo, setBalanceInfo] = useState({ TotalCredits: 0, TotalExpenses: 0, Balance: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [empCode] = useState(() => localStorage.getItem(STORAGE_KEYS.EMP_CODE) || '');
  const [empId] = useState(() => {
    const id = localStorage.getItem(STORAGE_KEYS.EMP_ID);
    return id ? parseInt(id, 10) : null;
  });
  const [empName] = useState(() => localStorage.getItem(STORAGE_KEYS.USER_NAME) || 'Self');

  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [selectedEmpName, setSelectedEmpName] = useState('');
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeeModalVisible, setEmployeeModalVisible] = useState(false);

  const getToken = () => localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

  const fetchBalance = useCallback(async (id) => {
    if (!id) return;
    try {
      const response = await fetch(`${BASE_URL}api/Expense/EMPACCredit/Balance/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (response.ok) setBalanceInfo(await response.json());
    } catch (_) {}
  }, []);

  const fetchEmployeesList = useCallback(async () => {
    setEmployeesLoading(true);
    try {
      const response = await fetch(`${BASE_URL}api/Employee/List`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (response.ok) setEmployees(await response.json());
    } catch (_) {} finally {
      setEmployeesLoading(false);
    }
  }, []);

  const fetchExpenses = useCallback(async (isRefresh = false, targetEmpId) => {
    const idToFetch = targetEmpId ?? selectedEmpId;
    if (!idToFetch) { setLoading(false); return; }
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const response = await fetch(`${BASE_URL}api/Expense/List?InsertedBy=${idToFetch}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (response.ok) setExpenses(await response.json());
      else showToast(`Failed to load expenses (${response.status})`, 'error');
      await fetchBalance(idToFetch);
    } catch (_) {
      showToast('Could not load expenses. Please check your connection.', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedEmpId, fetchBalance]);

  useEffect(() => {
    const init = async () => {
      const id = empId;
      setSelectedEmpId(id);
      setSelectedEmpName(empName);
      if (empCode?.toUpperCase() === 'A') fetchEmployeesList();
      if (id) fetchExpenses(false, id);
      else setLoading(false);
    };
    init();
  }, []); // eslint-disable-line

  const handleLogout = () => {
    showAlert('Logout', 'Are you sure you want to logout?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Logout', style: 'destructive', onPress: () => {
          localStorage.clear();
          onLogout?.();
        }
      },
    ]);
  };

  const handleDelete = (id) => {
    showAlert('Delete Expense', 'Are you sure you want to delete this expense?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive', onPress: async () => {
          try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}api/Expense/Delete/${id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (response.ok) {
              showToast('Expense deleted successfully.', 'success');
              fetchExpenses();
            } else {
              showToast(`Failed to delete expense (${response.status})`, 'error');
              setLoading(false);
            }
          } catch (_) {
            showToast('Could not delete expense.', 'error');
            setLoading(false);
          }
        }
      },
    ]);
  };

  const sections = groupExpensesByDate(expenses);

  if (loading) {
    return (
      <AppShell title="Expenses" rightAction={{ icon: '⏻', onClick: handleLogout, label: 'Logout' }}>
        <div className="loading-screen">
          <div className="spinner spinner--gold" />
          <p>Loading expenses...</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Expenses"
      rightAction={{ icon: '⏻', onClick: handleLogout, label: 'Logout' }}
      rightAction2={{ icon: '↻', onClick: () => fetchExpenses(true), label: 'Refresh' }}
    >
      {/* Employee Selector (admin only) */}
      {empCode?.toUpperCase() === 'A' && (
        <button
          className="emp-selector"
          onClick={() => setEmployeeModalVisible(true)}
        >
          <div className="emp-selector__info">
            <div className="emp-selector__label">Viewing Data For:</div>
            <div className="emp-selector__name">{selectedEmpName}</div>
          </div>
          <span className="emp-selector__arrow">›</span>
        </button>
      )}

      {/* Balance Card */}
      <div className="balance-card">
        <div className="balance-row">
          <button
            className="balance-item"
            onClick={() => { if (selectedEmpId) navigate('EmpCreditList', { empId: selectedEmpId }); }}
          >
            <div className="balance-label">Credits</div>
            <div className="balance-value" style={{ color: '#1a7a3c' }}>
              {formatINR(balanceInfo.TotalCredits).replace('₹ ', '')}
            </div>
          </button>
          <span className="balance-op">−</span>
          <div className="balance-item">
            <div className="balance-label">Expenses</div>
            <div className="balance-value" style={{ color: '#c62828' }}>
              {formatINR(balanceInfo.TotalExpenses).replace('₹ ', '')}
            </div>
          </div>
          <span className="balance-op">=</span>
          <div className="balance-item">
            <div className="balance-label">Balance</div>
            <div className="balance-value" style={{ color: '#C8973A', fontWeight: 700 }}>
              {formatINR(balanceInfo.Balance).replace('₹ ', '')}
            </div>
          </div>
        </div>
      </div>

      {/* Expense List */}
      {refreshing && (
        <div style={{ textAlign: 'center', padding: '12px', color: '#999', fontSize: 13 }}>
          Refreshing...
        </div>
      )}

      {sections.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: 60, color: '#aaa', fontSize: 15 }}>
          No expenses found.
        </div>
      ) : (
        <div className="expense-list" style={{ paddingBottom: 100 }}>
          {sections.map(section => (
            <div key={section.title}>
              {/* Section Header */}
              <div className="section-header">
                <span className="section-title">{section.title}</span>
                <span className="section-total" style={{ color: '#999' }}>
                  {section.total < 0 ? '−' : ''}{formatINR(Math.abs(section.total)).replace('₹ ', '')}
                </span>
              </div>
              {/* Items */}
              {section.data.map(item => {
                const catStyle = getCategoryIcon(item.ExpCatName);
                return (
                  <div
                    key={item.ID}
                    className="expense-item"
                    onClick={() => navigate('ExpenseForm', { expenseId: item.ID })}
                  >
                    <div className="expense-icon" style={{ background: catStyle.color }}>
                      {catStyle.icon}
                    </div>
                    <div className="expense-content">
                      <div className="expense-party">{item.ExpPartyName || 'No Party'}</div>
                      <div className="expense-cat">{item.ExpCatName || 'Other'}</div>
                    </div>
                    <div className="expense-amount" style={{ color: '#c62828' }}>
                      −{formatINR(item.Amount || 0).replace('₹ ', '')}
                    </div>
                    <button
                      className="expense-delete-btn"
                      onClick={e => { e.stopPropagation(); handleDelete(item.ID); }}
                      aria-label="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* FABs */}
      <div className="fab-container">
        {empCode?.toUpperCase() === 'A' && (
          <button className="fab fab--green" onClick={() => navigate('EmpCreditForm')}>
            <span>＋</span> Add Credit
          </button>
        )}
        <button className="fab fab--primary" onClick={() => navigate('ExpenseForm')}>
          <span>＋</span> Add Expense
        </button>
      </div>

      {/* Employee Modal */}
      <Modal
        visible={employeeModalVisible}
        onDismiss={() => setEmployeeModalVisible(false)}
        title="Select Employee"
      >
        {employeesLoading ? (
          <div className="loading-screen" style={{ minHeight: 120 }}>
            <div className="spinner spinner--gold" />
          </div>
        ) : (
          employees.map(emp => (
            <div
              key={emp.ID}
              className="list-item"
              onClick={() => {
                setSelectedEmpId(emp.ID);
                setSelectedEmpName(emp.EmpName);
                setEmployeeModalVisible(false);
                fetchExpenses(false, emp.ID);
              }}
            >
              <div className="list-item__text">
                <div className="list-item__title">{emp.EmpName}</div>
              </div>
              {selectedEmpId === emp.ID && <span className="list-item__check">✓</span>}
            </div>
          ))
        )}
      </Modal>

      <style>{`
        .emp-selector {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #f0f4f8;
          margin: 12px;
          padding: 10px 14px 10px 16px;
          border-radius: 12px;
          border: 1px solid #d1d9e6;
          cursor: pointer;
          width: calc(100% - 24px);
          text-align: left;
        }
        .emp-selector__label { font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
        .emp-selector__name { font-size: 15px; font-weight: 700; color: #333; }
        .emp-selector__arrow { font-size: 22px; color: #999; }
        .balance-card {
          background: #f8f9fa;
          padding: 16px;
          border-bottom: 1px solid #eee;
        }
        .balance-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .balance-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
        }
        .balance-label {
          font-size: 11px;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .balance-value {
          font-size: 15px;
          font-weight: 600;
        }
        .balance-op {
          font-size: 16px;
          color: #999;
          padding-top: 16px;
        }
        .section-header {
          display: flex;
          justify-content: space-between;
          padding: 12px 16px;
          background: #fff;
          border-bottom: 0.5px solid #eee;
          margin-top: 2px;
        }
        .section-title { font-size: 14px; color: #999; font-weight: 400; }
        .section-total { font-size: 14px; color: #999; font-weight: 400; }
        .expense-item {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          background: #fff;
          border-bottom: 0.5px solid #f0f0f0;
          cursor: pointer;
          transition: background 0.15s;
        }
        .expense-item:hover { background: #fafafa; }
        .expense-icon {
          width: 40px;
          height: 40px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
          margin-right: 12px;
        }
        .expense-content { flex: 1; min-width: 0; }
        .expense-party {
          font-size: 15px;
          font-weight: 500;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .expense-cat {
          font-size: 13px;
          color: #999;
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .expense-amount {
          font-size: 15px;
          font-weight: 600;
          flex-shrink: 0;
          margin-right: 4px;
        }
        .expense-delete-btn {
          background: none;
          border: none;
          font-size: 16px;
          padding: 4px;
          opacity: 0.5;
          cursor: pointer;
          transition: opacity 0.15s;
          flex-shrink: 0;
        }
        .expense-delete-btn:hover { opacity: 1; }
      `}</style>
    </AppShell>
  );
};

export default ExpenseListScreen;
